// Settings → Email: each person connects their Gmail once (an incremental Google grant: send, and read the headers
// of PO threads for vendor replies), may share a mailbox with other staff (a purchasing@ address), and staff choose
// who POs go from: one mailbox for everyone, or per vendor. Tokens never come here; only the mailbox's address.
import { Mail, Plus, Trash2 } from "lucide-react";
import { useCallback, useEffect, useMemo, useState } from "react";
import type { GmailAccount } from "../../../shared/db.gen.ts";
import { ago, personName } from "../lib/format.ts";
import { can, mutate } from "../lib/mutate.ts";
import { setSearch, useSearch } from "../lib/router.tsx";
import { me } from "../lib/session.ts";
import { useRows } from "../lib/sync.ts";
import { Badge, Button, Select } from "../ui/controls.tsx";
import { toast } from "../ui/toast.tsx";
import { gmailOffText, postJson } from "./po/PoEmail.tsx";

const ERRORS: Record<string, string> = {
  off: "Emailing POs isn't turned on on this server yet.",
  expired: "That took too long, or was started in another tab. Connect again.",
  cancelled: "Google didn't connect it (cancelled).",
  staff: "Only staff can connect a mailbox.",
  google: "Google didn't accept it. Connect again.",
};

async function disconnect(id: string, email: string) {
  if (!confirm(`Disconnect ${email}? HHL Ops stops sending from it and stops looking for vendor replies in it.`)) return;
  const { ok, body } = await postJson(`/api/gmail/accounts/${id}/disconnect`);
  if (ok) toast.ok(`${email} disconnected`);
  else toast.error(body.error ?? "Couldn't disconnect it");
}

function MailboxRow({ m, who }: { m: GmailAccount; who: (id: string) => string }) {
  const user = me();
  return (
    <div className="flex items-center gap-3 border-b border-line px-4 py-2.5 last:border-0">
      <Mail className="size-4 text-faint" />
      <div className="min-w-0 flex-1">
        <div className="font-medium">
          {m.name ? `${m.name} ` : ""}
          <span className="text-muted">{m.email}</span>
        </div>
        <div className="text-[12px] text-faint">
          {m.user_id !== user?.id && `${who(m.user_id)} connected it · `}
          connected {ago(m.connected_at)} · {m.last_used_at ? `last sent ${ago(m.last_used_at)}` : "nothing sent yet"}
          {m.status === "needs_reconnect" && <span className="text-danger"> · Google stopped accepting it: connect again ({m.last_error})</span>}
        </div>
      </div>
      {m.status === "needs_reconnect" ? <Badge tone="danger">Reconnect</Badge> : <Badge tone="ok">Connected</Badge>}
      {(m.user_id === user?.id || user?.role === "admin") && (
        <>
          <label className="inline-flex items-center gap-1.5 text-[12px]" title="Other staff may send POs from it (a shared purchasing@ mailbox)">
            <input
              type="checkbox"
              checked={m.shared}
              disabled={m.status !== "connected"}
              onChange={(e) => mutate("gmail_account.update", { id: m.id, patch: { shared: e.target.checked } })}
            />
            Staff may send from it
          </label>
          <button
            type="button"
            className="rounded p-1 text-faint hover:bg-hover hover:text-danger"
            title="Disconnect"
            onClick={() => disconnect(m.id, m.email)}
          >
            <Trash2 className="size-4" />
          </button>
        </>
      )}
    </div>
  );
}

export function EmailSettingsPage() {
  const user = me();
  const feature = user?.gmail;
  const mailboxes = useRows("gmail_account");
  const users = useRows("app_user");
  const vendors = useRows("vendor");
  const settings = useRows("po_email_settings")[0];
  const search = useSearch();
  const [vendorId, setVendorId] = useState("");

  // Back from Google.
  const connected = search.get("connected");
  const error = search.get("error");
  useEffect(() => {
    if (connected) toast.ok("Gmail connected");
    if (error) toast.error(ERRORS[error] ?? error);
    if (connected || error) setSearch({ connected: null, error: null });
  }, [connected, error]);

  const mine = mailboxes.filter((m) => m.user_id === user?.id && m.status !== "disconnected");
  const others = mailboxes.filter((m) => m.user_id !== user?.id && m.status !== "disconnected");
  const who = useCallback((id: string) => personName(users, id), [users]);
  const senderOptions = useMemo(() => {
    const emails = new Set(mailboxes.filter((m) => m.status !== "disconnected").map((m) => m.email.toLowerCase()));
    if (settings?.default_sender_email) emails.add(settings.default_sender_email);
    for (const v of vendors) if (v.po_sender_email) emails.add(v.po_sender_email);
    return [...emails].sort().map((e) => {
      const box = mailboxes.find((m) => m.email.toLowerCase() === e);
      const note =
        !box || box.status === "disconnected"
          ? " (not connected)"
          : box.status !== "connected"
            ? " (needs reconnecting)"
            : box.shared
              ? " (shared)"
              : ` (${who(box.user_id)} only)`;
      return { value: e, label: `${e}${note}` };
    });
  }, [mailboxes, settings?.default_sender_email, vendors, who]);
  const exceptions = vendors.filter((v) => v.po_sender_email).sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="h-full overflow-y-auto">
      <div className="mx-auto max-w-3xl px-8 py-8">
        <p className="text-muted">
          POs go to vendors from a Gmail mailbox: the PDF attached, the documents link in the message, and the vendor's replies linked back to the PO. HHL Ops
          only sends PO emails and vendor portal sign-in links, and reads who replied on PO threads (and Gmail's one-line preview); it never reads the rest of
          the mailbox. A sign-in link a vendor asks for on the portal goes from the mailbox their POs go from, only if it's shared.
        </p>

        {!feature?.enabled && <div className="mt-4 rounded-lg border border-warn/30 bg-warn-soft p-3 text-[13px]">{gmailOffText()}</div>}
        {feature?.fake && (
          <div className="mt-4 rounded-lg border border-line bg-surface-2 p-3 text-[13px] text-muted">
            Dev: Gmail is the in-memory fake (GMAIL_FAKE=1); nothing is really sent.
          </div>
        )}

        <div className="mt-6 flex items-center gap-2">
          <h2 className="font-semibold">Your Gmail</h2>
          {feature?.enabled && can("gmail_account.update") && (
            <Button
              size="sm"
              variant={mine.length ? "secondary" : "primary"}
              icon={<Plus className="size-3.5" />}
              className="ml-auto"
              onClick={() => location.assign("/api/gmail/connect")}
            >
              {mine.length ? "Connect another (e.g. purchasing@)" : "Connect Gmail"}
            </Button>
          )}
        </div>
        <div className="mt-2 overflow-hidden rounded-lg border border-line bg-surface">
          {!mine.length && (
            <div className="p-4 text-muted">
              Not connected. Google asks you to let HHL Ops send email for you and read your mail; it only ever reads who replied on PO threads, when, and
              Gmail's preview line. Your sign-in doesn't change.
            </div>
          )}
          {mine.map((m) => (
            <MailboxRow key={m.id} m={m} who={who} />
          ))}
        </div>

        {others.length > 0 && (
          <>
            <h2 className="mt-8 mb-2 font-semibold">Other mailboxes</h2>
            <div className="overflow-hidden rounded-lg border border-line bg-surface">
              {others.map((m) => (
                <MailboxRow key={m.id} m={m} who={who} />
              ))}
            </div>
          </>
        )}

        <h2 className="mt-8 font-semibold">Who POs are sent from</h2>
        <p className="mt-1 text-[13px] text-muted">The person sending can still pick another mailbox they may use in the send dialog.</p>
        <div className="mt-3 rounded-lg border border-line bg-surface p-4">
          <label className="flex items-center gap-3">
            <span className="w-40 shrink-0 text-muted">For every vendor</span>
            <Select
              value={settings?.default_sender_email ?? ""}
              disabled={!settings || !can("po_email_settings.update")}
              options={[{ value: "", label: "The person sending (their own Gmail)" }, ...senderOptions]}
              onChange={(e) => settings && mutate("po_email_settings.update", { id: settings.id, patch: { default_sender_email: e.target.value } })}
              className="h-8 flex-1"
            />
          </label>
          <div className="mt-4 text-[12px] font-medium tracking-wide text-faint uppercase">Vendors sent from another mailbox</div>
          <div className="mt-1 divide-y divide-line">
            {exceptions.map((v) => (
              <div key={v.id} className="flex items-center gap-3 py-1.5">
                <span className="w-40 shrink-0 truncate">{v.short_name || v.name}</span>
                <Select
                  value={v.po_sender_email}
                  options={senderOptions}
                  disabled={!can("vendor.update_po_sender")}
                  onChange={(e) => mutate("vendor.update_po_sender", { id: v.id, patch: { po_sender_email: e.target.value } })}
                  className="h-8 flex-1"
                />
                <button
                  type="button"
                  className="rounded p-1 text-faint hover:bg-hover hover:text-danger"
                  title="Use the default"
                  onClick={() => mutate("vendor.update_po_sender", { id: v.id, patch: { po_sender_email: "" } })}
                >
                  <Trash2 className="size-4" />
                </button>
              </div>
            ))}
          </div>
          {can("vendor.update_po_sender") && senderOptions.length > 0 && (
            <div className="mt-2 flex items-center gap-2">
              <Select
                value={vendorId}
                options={[
                  { value: "", label: "Add a vendor…" },
                  ...vendors
                    .filter((v) => !v.archived && !v.po_sender_email)
                    .sort((a, b) => a.name.localeCompare(b.name))
                    .map((v) => ({ value: v.id, label: v.name })),
                ]}
                onChange={(e) => setVendorId(e.target.value)}
                className="h-8 w-64"
              />
              <Select
                value=""
                disabled={!vendorId}
                options={[{ value: "", label: "…sent from" }, ...senderOptions]}
                onChange={(e) => {
                  if (!vendorId || !e.target.value) return;
                  mutate("vendor.update_po_sender", { id: vendorId, patch: { po_sender_email: e.target.value } });
                  setVendorId("");
                }}
                className="h-8 flex-1"
              />
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
